import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import EventCard from "../components/events/EventCard";
import { eventApi } from "../services/api";
import "./EventsView.css";

function EventsView() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);

  const rawPage = searchParams.get("page");
  const currentPage = parsePageParameter(rawPage);

  const pageItems = pagination
    ? buildPageItems(currentPage, pagination.totalPages)
    : [];

  const setPage = useCallback(
    (page, replace = false) => {
      setSearchParams(
        (previousParams) => {
          const nextParams = new URLSearchParams(previousParams);
          nextParams.set("page", String(page));
          return nextParams;
        },
        { replace }
      );
    },
    [setSearchParams]
  )

  useEffect(() => {
    const promotionId = 1; // We only have one single promotion in the system, so it will be hardcoded until more are added.
    const controller = new AbortController();

    let cancelled = false;
    let redirecting = false;

    setLoading(true);
    setError("");
    setEvents([]);

    eventApi
      .getEventsByPromotionId(promotionId, {
        page: currentPage,
        signal: controller.signal,
      })
      .then((data) => {
        if (cancelled) return;

        const finalPage = data.totalPages > 0 ? data.totalPages : 1;

        if (currentPage > finalPage) {
          redirecting = true;
          setPage(finalPage, true);
          return;
        }

        const mappedEvents = (data.events || []).map(
          (event) => ({
            id: event.event_id,
            name: event.name,
            location: event.location,
            date: event.date_str,
            imageUrl: event.posterUrl || event.imageUrl,
          })
        );

        setEvents(mappedEvents);
        setPagination({
          page: data.page,
          pageSize: data.pageSize,
          totalItems: data.totalItems,
          totalPages: data.totalPages,
          hasPrevious: data.hasPrevious,
          hasNext: data.hasNext,
        });
      })
      .catch((err) => {
        if (cancelled || err.name === "AbortError") return;

        console.error("Failed to load events:", err);
        setPagination(null);
        setError("Unable to load events right now.");
      })
      .finally(() => {
        if (!cancelled && !redirecting) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [currentPage, setPage]);

  useEffect(() => {
    if (
      rawPage !== null &&
      rawPage !== String(currentPage)
    ) {
      setPage(currentPage, true);
    }
  }, [rawPage, currentPage, setPage]);
  
  return (
    <div className="container-fluid events-view">
      <div className="row" aria-busy={loading}>
        {error && (
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        )}

        {loading && (
          <div className="col-12 events-status">
            <div
              className="spinner-border"
              role="status"
              aria-label="Loading events"
            />
            <span>Loading events…</span>
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className="col-12 events-status">
            <p>No events found.</p>
          </div>
        )}

        {!loading &&
          !error &&
          events.map((event) => (
            <div
              key={event.id}
              className="col-12 col-md-6 col-lg-4"
            >
              <EventCard event={event} />
            </div>
          ))}
      </div>  

      {pagination && pagination.totalPages > 1 && (
          <nav 
            className="events-pagination"
            aria-label="Event pages"
          >
            <p className="pagination">
              Page {currentPage} of {pagination.totalPages}
            </p>

            <ul className="pagination">
              <li
                className={
                  `page-item ${
                    !pagination.hasPrevious || loading ? "disabled" : ""
                  }`
                }
              >
                <button
                  type="button"
                  className="page-link"
                  disabled={!pagination.hasPrevious || loading}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </button>
              </li>

              {pageItems.map((item) => {
                if (typeof item === "string") {
                  return (
                    <li
                      key={item}
                      className="page-item disabled"
                      aria-hidden="true"
                    >
                      <span className="page-link">...</span>
                    </li>
                  );
                }

                const isCurrentPage = item === currentPage;

                return (
                  <li
                    key={item}
                    className={
                      `page-item ${isCurrentPage ? "active" : ""}`
                    }
                  >
                    <button
                      type="button"
                      className="page-link"
                      disabled={loading}
                      aria-current={isCurrentPage ? "page" : undefined }
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </button>
                  </li>
                );
              })}

              <li
                className={
                  `page-item ${
                    !pagination.hasNext || loading
                      ? "disabled"
                      : ""
                  }`
                }
              >
                <button
                  type="button"
                  className="page-link"
                  disabled={!pagination.hasNext || loading}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        )}
    </div>
  );
}

function parsePageParameter(value) {
  if (value === null) return 1;

  if (!/^\d+$/.test(value)) return 1;

  const parsedPage = Number(value);

  if (!Number.isSafeInteger(parsedPage) || parsedPage < 1) return 1;

  return parsedPage;
}

function buildPageItems(currentPage, totalPages) {
  const candidatePages = [
    1,
    currentPage - 1,
    currentPage,
    currentPage + 1,
    totalPages,
  ];

  const pages = [...new Set(candidatePages)]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  const items = [];

  pages.forEach((page, index) => {
    const previousPage = pages[index - 1];

    if (
      previousPage !== undefined &&
      page - previousPage > 1
    ) {
      items.push(`ellipsis-${previousPage}-${page}`);
    }

    items.push(page);
  });

  return items;
}

export default EventsView;
